import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IconsProviderModule } from './icons-provider.module';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { AdminLayoutComponent } from './layout/admin-layout/admin-layout.component';
import { UsersComponent } from './components/users/users.component';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzTableModule } from 'ng-zorro-antd/table';
import {
  ChevronLeftCircle,
  ChevronRightCircle,
  File,
  FolderDot,
  FolderInput,
  FolderSymlink,
  LucideAngularModule,
  Menu,
  Users2,
} from 'lucide-angular';
import { CustomBreadcrumbComponent } from './components/common/custom-breadcrumb/custom-breadcrumb.component';
import { AddEditRolesComponent } from './components/roles/add-edit-roles/add-edit-roles.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { AuthService } from './modules/auth/services/auth.service';
import { AuthGuard } from './core/guards/auth.guard';
import { EncryptionService } from './core/services/encryption.service';
import { TokenInterceptor } from './core/interceptors/http.interceptor';
import { ViewRolesComponent } from './components/roles/view-roles/view-roles.component';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { AddEditUserComponent } from './components/users/add-edit-user/add-edit-user.component';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { UserProfileComponent } from './components/users/user-profile/user-profile.component';
import { NzNotificationModule } from 'ng-zorro-antd/notification';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { FleetComponent } from './components/fleet/fleet.component';
import { AddEditFleetComponent } from './components/fleet/add-edit-fleet/add-edit-fleet.component';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { SingleViewFleetComponent } from './components/fleet/single-view-fleet/single-view-fleet.component';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { HasAnyPrivilegeDirective } from './core/directives/has-any-privilege.directive';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { PrivilegeService } from './modules/auth/services/privilege.service';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { ViewInventoryComponent } from './components/inventory/view-inventory/view-inventory.component';
import { ViewRepairsComponent } from './components/Repairs/view-repairs/view-repairs.component';
import { AddEditInventoryComponent } from './components/inventory/add-edit-inventory/add-edit-inventory.component';
import { SingleViewInventoryComponent } from './components/inventory/single-view-inventory/single-view-inventory.component';
import { NotesAttachmentsComponent } from './components/notes-attachments/notes-attachments/notes-attachments.component';
import { SingleViewRepairsComponent } from './components/Repairs/single-view-repairs/single-view-repairs.component';
import { AddEditRepairsComponent } from './components/Repairs/add-edit-repairs/add-edit-repairs.component';

import { AddEditServiceComponent } from './components/fleet-services/add-edit-service/add-edit-service.component';
import { SingleViewServiceComponent } from './components/fleet-services/single-view-service/single-view-service.component';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { AddEditAccidentComponent } from './components/accidents/add-edit-accident/add-edit-accident.component';
import { ViewAccidentsComponent } from './components/accidents/view-accidents/view-accidents.component';
import { SingleViewAccidentComponent } from './components/accidents/single-view-accident/single-view-accident.component';
import { TableSkeletonTemplateComponent } from './components/shared/table-skeleton-template/table-skeleton-template.component';
import { AddEditDriversComponent } from './components/drivers/add-edit-drivers/add-edit-drivers.component';
import { VehicleDetailsFormComponent } from './components/shared/vehicle-details-form/vehicle-details-form.component';
import { ViewDriversComponent } from './components/drivers/view-drivers/view-drivers.component';
import { ViewServicesComponent } from './components/fleet-services/view-services/view-services.component';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { SingleViewDriverComponent } from './components/drivers/single-view-driver/single-view-driver.component';
import { AddEditSupplierComponent } from './components/supplier/add-edit-supplier/add-edit-supplier.component';
import { ViewSuppliersComponent } from './components/supplier/view-suppliers/view-suppliers.component';
import { ViewSingleSupplierComponent } from './components/supplier/view-single-supplier/view-single-supplier.component';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzCarouselModule } from 'ng-zorro-antd/carousel';
import { ViewCustomerComponent } from './components/customer/view-customer/view-customer.component';
import { AddEditCustomerComponent } from './components/customer/add-edit-customer/add-edit-customer.component';
import { ViewSingleCustomerComponent } from './components/customer/view-single-customer/view-single-customer.component';
import { UnitPipe } from './core/pipes/unit.pipe';
import { SingleViewSkeletonTemplateComponent } from './components/shared/single-view-skeleton-template/single-view-skeleton-template.component';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { AddSupplierPopupComponent } from './components/shared/add-supplier-popup/add-supplier-popup.component';
import { AddEditAgentComponent } from './components/agents/add-edit-agent/add-edit-agent.component';
import { ViewAgentsComponent } from './components/agents/view-agents/view-agents.component';
import { AddEditBookingComponent } from './components/bookings/add-edit-booking/add-edit-booking.component';
import { ViewBookingComponent } from './components/bookings/view-booking/view-booking.component';
import { ViewSingleBookingComponent } from './components/bookings/view-single-booking/view-single-booking.component';
import { RentReservationComponent } from './components/reservations/rent-reservation/rent-reservation.component';
import { AddEditRentReservationComponent } from './components/reservations/rent-reservation/add-edit-rent-reservation/add-edit-rent-reservation.component';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { AddAgentPopupComponent } from './components/shared/add-agent-popup/add-agent-popup.component';
import { AddCustomerPopupComponent } from './components/shared/add-customer-popup/add-customer-popup.component';
import { SingleViewRentReservationComponent } from './components/reservations/rent-reservation/single-view-rent-reservation/single-view-rent-reservation.component';
import { AddEditOtherReservationComponent } from './components/reservations/other-reservation/add-edit-other-reservation/add-edit-other-reservation.component';
import { OtherReservationComponent } from './components/reservations/other-reservation/other-reservation.component';
import { SingleViewOtherReservationComponent } from './components/reservations/other-reservation/single-view-other-reservation/single-view-other-reservation.component';
import { AddDriverPopupComponent } from './components/shared/add-driver-popup/add-driver-popup.component';
import { CurrentFuelAndMileageUpdatePopupComponent } from './components/shared/current-fuel-and-mileage-update-popup/current-fuel-and-mileage-update-popup.component';
import { canDeactivateGuardService } from './core/guards/deactivate.gaurd';
import { SingleViewCustomerPopupComponent } from './components/shared/single-view-customer-popup/single-view-customer-popup.component';
import { SingleViewDriverPopupComponent } from './components/shared/single-view-driver-popup/single-view-driver-popup.component';
import { SingleViewSupplierPopupComponent } from './components/shared/single-view-supplier-popup/single-view-supplier-popup.component';
import { CurrencyConfigurationComponent } from './components/currency-configuration/currency-configuration.component';
import { EditViewSkeletonTemplateComponent } from './components/shared/edit-view-skeleton-template/edit-view-skeleton-template.component';
import { PaymentPopupComponent } from './components/shared/payment-popup/payment-popup.component';
import { GenerateInventoryReportPopupComponent } from './components/shared/generate-inventory-report-popup/generate-inventory-report-popup.component';
import { GeneratePagingBoardPopupComponent } from './components/shared/generate-paging-board-popup/generate-paging-board-popup.component';
import { GenerateReportModalComponent } from './components/shared/generate-report-modal/generate-report-modal.component';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { ViewAgentBookingsComponent } from './components/agents/view-agent-bookings/view-agent-bookings.component';
import { SystemLogComponent } from './components/system-log/system-log.component';
import { BackOptionSectionComponent } from './components/shared/back-option-section/back-option-section.component';
import { EmailContentDrawerComponent } from './components/shared/email-content-drawer/email-content-drawer.component';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { SafeHtmlPipe } from './core/pipes/safe-html.pipe';
import { MultiplePagingBoardPopupComponent } from './components/shared/multiple-paging-board-popup/multiple-paging-board-popup.component';

registerLocaleData(en);

@NgModule({
  declarations: [
    AppComponent,
    AdminLayoutComponent,
    UsersComponent,
    CustomBreadcrumbComponent,
    AddEditRolesComponent,
    ViewRolesComponent,
    AddEditUserComponent,
    UserProfileComponent,
    FleetComponent,
    AddEditFleetComponent,
    SingleViewFleetComponent,
    AddEditServiceComponent,
    HasAnyPrivilegeDirective,
    ViewInventoryComponent,
    ViewRepairsComponent,
    AddEditInventoryComponent,
    SingleViewInventoryComponent,
    NotesAttachmentsComponent,
    SingleViewRepairsComponent,
    SingleViewServiceComponent,
    AddEditAccidentComponent,
    ViewAccidentsComponent,
    SingleViewAccidentComponent,
    AddEditRepairsComponent,
    ViewServicesComponent,
    TableSkeletonTemplateComponent,
    AddEditDriversComponent,
    VehicleDetailsFormComponent,
    ViewDriversComponent,
    SingleViewDriverComponent,
    AddEditSupplierComponent,
    ViewSuppliersComponent,
    ViewSingleSupplierComponent,
    ViewCustomerComponent,
    AddEditCustomerComponent,
    ViewSingleCustomerComponent,
    UnitPipe,
    SingleViewSkeletonTemplateComponent,
    AddSupplierPopupComponent,
    AddEditAgentComponent,
    ViewAgentsComponent,
    AddEditBookingComponent,
    ViewBookingComponent,
    ViewSingleBookingComponent,
    RentReservationComponent,
    AddEditRentReservationComponent,
    AddAgentPopupComponent,
    AddCustomerPopupComponent,
    SingleViewRentReservationComponent,
    AddEditOtherReservationComponent,
    OtherReservationComponent,
    SingleViewOtherReservationComponent,
    AddDriverPopupComponent,
    CurrentFuelAndMileageUpdatePopupComponent,
    SingleViewCustomerPopupComponent,
    SingleViewDriverPopupComponent,
    SingleViewSupplierPopupComponent,
    CurrencyConfigurationComponent,
    EditViewSkeletonTemplateComponent,
    PaymentPopupComponent,
    GenerateInventoryReportPopupComponent,
    GeneratePagingBoardPopupComponent,
    GenerateReportModalComponent,
    ViewAgentBookingsComponent,
    SystemLogComponent,
    BackOptionSectionComponent,
    EmailContentDrawerComponent,
    SafeHtmlPipe,
    MultiplePagingBoardPopupComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    HttpClientModule,
    BrowserAnimationsModule,
    IconsProviderModule,
    LucideAngularModule.pick({
      Menu,
      ChevronLeftCircle,
      ChevronRightCircle,
      FolderSymlink,
      FolderInput,
      FolderDot,
      Users2,
      File,
    }),
    NzLayoutModule,
    NzMenuModule,
    NzCardModule,
    NzButtonModule,
    NzBreadCrumbModule,
    NzTableModule,
    NzInputModule,
    NzFormModule,
    FormsModule,
    ReactiveFormsModule,
    NzDividerModule,
    NzCheckboxModule,
    NzDropDownModule,
    NzPopconfirmModule,
    NzAvatarModule,
    NzImageModule,
    NzUploadModule,
    NzSelectModule,
    NzSwitchModule,
    NzCollapseModule,
    NzBadgeModule,
    NzTagModule,
    NzNotificationModule,
    NzModalModule,
    NzTabsModule,
    NzDatePickerModule,
    NgxWebstorageModule.forRoot(),
    NzSkeletonModule,
    NzToolTipModule,
    NzAlertModule,
    NzMessageModule,
    NzInputNumberModule,
    NzCarouselModule,
    NzSpaceModule,
    NzTimePickerModule,
    NzPaginationModule,
    NzDrawerModule,
  ],
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    EncryptionService,
    AuthGuard,
    AuthService,
    PrivilegeService,
    canDeactivateGuardService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
